import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import { useToast } from "../../../shared/components/SharedUI";
import { getAvisosPago, crearAvisoPago } from "../services/portalService";

const ESTADO_COLORS = {
  PENDIENTE: { bg:"rgba(245,158,11,0.1)",  border:"rgba(245,158,11,0.3)",  text:"#f59e0b", label:"Pendiente" },
  VALIDO:    { bg:"rgba(74,222,128,0.1)",  border:"rgba(74,222,128,0.3)",  text:"#4ade80", label:"Válido"    },
  NO_VALIDO: { bg:"rgba(248,113,113,0.1)", border:"rgba(248,113,113,0.3)", text:"#f87171", label:"No válido" },
};

function Badge({ estado }) {
  const c = ESTADO_COLORS[estado] || { bg:"rgba(156,163,175,0.1)", border:"rgba(156,163,175,0.3)", text:"#9ca3af", label: estado };
  return (
    <span style={{ background:c.bg, border:`1px solid ${c.border}`, color:c.text, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700 }}>
      {c.label}
    </span>
  );
}

export default function PortalAvisosPago() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const { showToast, toastEl } = useToast();

  const [avisos,  setAvisos]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("nuevo");
  const [detalle, setDetalle] = useState("");
  const [file,    setFile]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const fileRef = useRef();

  const filialId = localStorage.getItem("filial_id");

  function load() {
    getAvisosPago().then(d => setAvisos(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  // Si el admin valida/rechaza el aviso mientras estamos en esta pantalla,
  // refrescamos solos en vez de esperar a que la persona recargue a mano.
  useEffect(() => {
    function onDataChanged(e) {
      if (e.detail?.tipo === "PAGO") load();
    }
    window.addEventListener("portal:data-changed", onDataChanged);
    return () => window.removeEventListener("portal:data-changed", onDataChanged);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { showToast(t("portal.comprobanteRequerido"), false); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("comprobante", file);
      fd.append("detalle", detalle);
      fd.append("filial", filialId);
      await crearAvisoPago(fd);
      showToast(t("portal.avisoPagoEnviado"), true);
      setDetalle(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
      setTimeout(() => setTab("historial"), 1200);
    } catch (e) {
      try { const d = JSON.parse(e.message); showToast(Object.values(d).flat().join(". "), false); }
      catch { showToast(e.message, false); }
    } finally { setSaving(false); }
  }

  // Hero theme vars — amber/yellow palette for payments
  const heroBg    = isLight ? "linear-gradient(135deg,#fffbeb 0%,#fff7ed 50%,#fefce8 100%)" : "linear-gradient(135deg,#1c1708 0%,#0f172a 60%,#1a130d 100%)";
  const heroText  = isLight ? "#0f172a" : "#ffffff";
  const heroSub   = isLight ? "rgba(15,23,42,0.5)" : "rgba(255,255,255,0.45)";
  const heroBorder= isLight ? "rgba(245,158,11,0.35)" : "rgba(245,158,11,0.2)";
  const heroShadow= isLight ? "0 4px 24px rgba(245,158,11,0.15),0 1px 3px rgba(0,0,0,0.06)" : "0 0 0 1px rgba(255,255,255,0.03),0 16px 48px rgba(0,0,0,.5)";

  return (
    <>
      {toastEl}
      <style>{`
        @keyframes papFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        .pap-upload:hover { border-color: #f97316 !important; background: rgba(249,115,22,0.08) !important; }
        .pap-aviso-card { transition: box-shadow .15s, transform .15s !important; }
        .pap-aviso-card:hover { transform: translateY(-2px) !important; box-shadow: 0 8px 24px rgba(0,0,0,.12) !important; }
      `}</style>
      <div style={{ padding:"28px 28px 48px", animation:"papFadeUp .4s ease both" }}>

        {/* ── Hero ── */}
        <div style={{
          borderRadius:16, marginBottom:24, position:"relative", overflow:"hidden",
          background:heroBg, border:`1px solid ${heroBorder}`, boxShadow:heroShadow,
        }}>
          <div style={{ position:"absolute", top:-50, right:-50, width:220, height:220, borderRadius:"50%", background:`radial-gradient(circle,${isLight?"rgba(245,158,11,0.12)":"rgba(245,158,11,0.15)"} 0%,transparent 70%)`, pointerEvents:"none" }} />
          <div style={{ position:"relative", padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ width:32, height:32, borderRadius:9, flexShrink:0, background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.35)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className="fa-solid fa-money-bill-transfer" style={{ color:"#f59e0b", fontSize:14 }} />
              </div>
              <div>
                <span style={{ fontSize:13, fontWeight:700, color:"#f59e0b", letterSpacing:0.5, display:"block", marginBottom:6 }}>{t("portal.avisosPago")}</span>
                <h1 style={{ fontSize:"clamp(18px,2.5vw,26px)", fontWeight:800, color:heroText, margin:"0 0 4px" }}>{t("portal.avisosPago")}</h1>
                <p style={{ margin:0, fontSize:13, color:heroSub }}>{t("portal.avisosPagoDesc")}</p>
              </div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              {[["nuevo","fa-plus-circle",t("portal.nuevoAviso")],["historial","fa-clock-rotate-left",t("portal.historial")]].map(([id,icon,label])=>(
                <button key={id} onClick={()=>setTab(id)} style={{
                  padding:"9px 16px", borderRadius:10, border:"none", cursor:"pointer",
                  fontWeight:700, fontSize:13, display:"flex", alignItems:"center", gap:7,
                  background: tab===id ? "#f97316" : isLight?"rgba(0,0,0,0.06)":"rgba(255,255,255,0.1)",
                  color: tab===id ? "#fff" : heroText,
                  boxShadow: tab===id ? "0 4px 14px rgba(249,115,22,0.4)" : "none",
                  transition:"all .15s",
                }}>
                  <i className={`fa-solid ${icon}`} style={{ fontSize:12 }} /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── TAB: Nuevo aviso ── */}
        {tab === "nuevo" && (
          <div>
            <form onSubmit={handleSubmit}>
              {/* File upload */}
              <div style={{ background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:14, padding:"24px", marginBottom:16, boxShadow: isLight?"0 2px 8px rgba(0,0,0,.05)":"0 2px 12px rgba(0,0,0,.2)" }}>
                <label style={{ display:"block", fontSize:12, fontWeight:700, color:"var(--text-secondary)", marginBottom:10, letterSpacing:0.8, textTransform:"uppercase" }}>
                  {t("portal.comprobante")} <span style={{color:"#f87171"}}>*</span>
                </label>
                <div className="pap-upload" onClick={() => fileRef.current?.click()} style={{
                  border:`2px dashed ${file?"#f97316":"var(--border-input)"}`,
                  borderRadius:12, padding:"32px 20px", textAlign:"center", cursor:"pointer",
                  marginBottom:20, background: file?"rgba(249,115,22,0.06)":"transparent",
                  transition:"all .2s",
                }}>
                  <div style={{ width:60, height:60, borderRadius:16, margin:"0 auto 14px", background: file?"rgba(249,115,22,0.15)":"rgba(156,163,175,0.1)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <i className={`fa-solid ${file?"fa-file-check":"fa-cloud-arrow-up"}`}
                      style={{ fontSize:26, color:file?"#f97316":"var(--text-secondary)" }} />
                  </div>
                  <p style={{ margin:0, fontSize:14, color:file?"#f97316":"var(--text-primary)", fontWeight:700 }}>
                    {file ? file.name : t("portal.subirComprobante")}
                  </p>
                  {!file && <p style={{ margin:"6px 0 0", fontSize:12, color:"var(--text-secondary)" }}>{t("portal.formatosPermitidos")}</p>}
                  {file && (
                    <button type="button" onClick={e=>{e.stopPropagation();setFile(null);if(fileRef.current)fileRef.current.value="";}} style={{
                      marginTop:10, background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.3)",
                      color:"#f87171", borderRadius:7, padding:"4px 12px", cursor:"pointer", fontSize:12, fontWeight:600,
                    }}>
                      {t("common.limpiar")}
                    </button>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*,.pdf" style={{display:"none"}} onChange={e=>setFile(e.target.files[0]||null)} />

                <label style={{ display:"block", fontSize:12, fontWeight:700, color:"var(--text-secondary)", marginBottom:8, letterSpacing:0.8, textTransform:"uppercase" }}>
                  {t("portal.detallePago")} <span style={{color:"#f87171"}}>*</span>
                </label>
                <textarea value={detalle} onChange={e=>setDetalle(e.target.value)}
                  required rows={4} placeholder={t("portal.detallePlaceholder")}
                  className="form-control" style={{ width:"100%", resize:"vertical", boxSizing:"border-box" }} />
              </div>

              <button type="submit" disabled={saving} style={{
                width:"100%", padding:"14px", borderRadius:12, border:"none",
                background:"#f97316", color:"#fff", fontWeight:800, fontSize:15,
                cursor:saving?"not-allowed":"pointer", opacity:saving?.7:1,
                boxShadow:"0 6px 20px rgba(249,115,22,0.4)",
                transition:"opacity .15s",
              }}>
                <i className="fa-solid fa-paper-plane" style={{ marginRight:8 }} />
                {saving ? t("common.cargando") : t("portal.enviarAviso")}
              </button>
            </form>
          </div>
        )}

        {/* ── TAB: Historial ── */}
        {tab === "historial" && (
          <div>
            {loading ? (
              <p style={{ textAlign:"center", color:"var(--text-secondary)" }}>{t("common.cargando")}</p>
            ) : avisos.length === 0 ? (
              <div style={{ textAlign:"center", color:"var(--text-secondary)", padding:56, background:"var(--bg-card)", borderRadius:16, border:"1px solid var(--border-card)" }}>
                <i className="fa-solid fa-inbox" style={{ fontSize:40, opacity:.3, display:"block", marginBottom:12 }} />
                <p style={{ margin:0, fontSize:14 }}>{t("portal.sinAvisosPago")}</p>
              </div>
            ) : (
              avisos.map(a => {
                const ec = ESTADO_COLORS[a.estado];
                return (
                  <div key={a.id} className="pap-aviso-card" style={{
                    background:"var(--bg-card)", border:`1px solid ${ec?ec.border:"var(--border-card)"}`,
                    borderLeft:`4px solid ${ec?ec.text:"var(--border-card)"}`,
                    borderRadius:14, padding:"18px 20px", marginBottom:12,
                    boxShadow: isLight?"0 2px 8px rgba(0,0,0,.05)":"0 2px 10px rgba(0,0,0,.2)",
                  }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ width:36, height:36, borderRadius:9, background: ec?ec.bg:"var(--hover-bg)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <i className={`fa-solid ${a.estado==="VALIDO"?"fa-circle-check":a.estado==="NO_VALIDO"?"fa-circle-xmark":"fa-clock"}`}
                            style={{ color:ec?ec.text:"var(--text-secondary)", fontSize:15 }} />
                        </div>
                        <div>
                          <div style={{ fontSize:12, color:"var(--text-secondary)" }}>{a.created_at?.split("T")[0]}</div>
                          <Badge estado={a.estado} />
                        </div>
                      </div>
                      {a.comprobante_url && (
                        <a href={a.comprobante_url} target="_blank" rel="noreferrer" style={{
                          display:"inline-flex", alignItems:"center", gap:6,
                          background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.3)",
                          color:"#f97316", padding:"6px 12px", borderRadius:8,
                          textDecoration:"none", fontWeight:700, fontSize:12,
                        }}>
                          <i className="fa-solid fa-paperclip" /> {t("portal.verComprobante")}
                        </a>
                      )}
                    </div>
                    <p style={{ margin:0, fontSize:14, color:"var(--text-primary)", lineHeight:1.5 }}>{a.detalle}</p>
                    {a.notas_admin && (
                      <div style={{
                        marginTop:10, padding:"10px 12px", borderRadius:9,
                        background: a.estado === "NO_VALIDO" ? "rgba(248,113,113,0.08)" : "rgba(74,222,128,0.08)",
                        borderLeft: `3px solid ${a.estado === "NO_VALIDO" ? "#f87171" : "#4ade80"}`,
                      }}>
                        <p style={{
                          margin:"0 0 3px", fontSize:11, fontWeight:700, letterSpacing:0.5,
                          textTransform:"uppercase",
                          color: a.estado === "NO_VALIDO" ? "#f87171" : "#4ade80",
                        }}>
                          {a.estado === "NO_VALIDO" ? "Motivo del rechazo" : "Nota del administrador"}
                        </p>
                        <p style={{ margin:0, fontSize:13, color:"var(--text-primary)", lineHeight:1.5 }}>
                          {a.notas_admin}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </>
  );
}
